import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowRight, FaSave, FaTimes } from 'react-icons/fa';
import { useTitle } from '../../../context/TitleContext';
import { createPromoCode, clearError, clearSuccess } from '../../../redux/Slices/PromoCodesSlice';
import { showSuccessToast, showErrorToast } from '../../../shared/utils/toastHelper';
import { ToastContainer } from 'react-toastify';

const PharmacyAddPromoCode = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();

  const { loading, error, success } = useSelector(state => state.promoCodes);

  const [formData, setFormData] = useState({
    code: '',
    discount_percent: '',
    min_order: '',
    max_order: '',
    usage_limit: '',
    starts_at: '',
    expires_at: '',
    status: 1
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    setTitle('إضافة برومو كود');
  }, [setTitle]);

  useEffect(() => {
    if (error) {
      showErrorToast(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (success) {
      showSuccessToast(success);
      dispatch(clearSuccess());
      navigate('/pharmacy-promo-codes');
    }
  }, [success, dispatch, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // منع القيم السالبة للخصم والحد الأدنى
    if ((name === 'discount_percent' || name === 'min_order' || name === 'max_order') && parseFloat(value) < 0) {
      return;
    }
    
    // منع الكسور في نسبة الخصم - فقط أرقام صحيحة
    if (name === 'discount_percent' && value.includes('.')) {
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // مسح الخطأ عند التعديل
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = 'كود البرومو مطلوب';
    }

    if (!formData.discount_percent) {
      newErrors.discount_percent = 'نسبة الخصم مطلوبة';
    } else {
      const discount = parseInt(formData.discount_percent);
      if (isNaN(discount) || discount < 1 || discount > 100) {
        newErrors.discount_percent = 'نسبة الخصم يجب أن تكون بين 1% و 100%';
      }
    }

    if (formData.min_order && parseFloat(formData.min_order) < 0) {
      newErrors.min_order = 'الحد الأدنى للطلب لا يمكن أن يكون سالب';
    }

    if (formData.max_order && parseFloat(formData.max_order) < 0) {
      newErrors.max_order = 'الحد الأقصى للطلب لا يمكن أن يكون سالب';
    } else if (formData.max_order && formData.min_order && parseFloat(formData.max_order) < parseFloat(formData.min_order)) {
      newErrors.max_order = 'الحد الأقصى يجب أن يكون أكبر من أو يساوي الحد الأدنى';
    }

    if (!formData.starts_at) {
      newErrors.starts_at = 'تاريخ البداية مطلوب';
    }

    if (!formData.expires_at) {
      newErrors.expires_at = 'تاريخ الانتهاء مطلوب';
    }

    if (formData.starts_at && formData.expires_at) {
      const startDate = new Date(formData.starts_at);
      const endDate = new Date(formData.expires_at);
      if (startDate >= endDate) {
        newErrors.expires_at = 'تاريخ الانتهاء يجب أن يكون بعد تاريخ البداية';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const submitData = {
      ...formData,
      discount_percent: parseInt(formData.discount_percent),
      min_order: formData.min_order ? parseFloat(formData.min_order) : 0,
      max_order: formData.max_order ? parseFloat(formData.max_order) : null,
      usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null,
      status: parseInt(formData.status)
    };

    dispatch(createPromoCode(submitData));
  };

  const handleKeyDown = (e) => {
    // منع إدخال الرموز السالبة والموجبة
    if (e.key === '-' || e.key === '+') {
      e.preventDefault();
    }
  };

  return (
    <div className="container-fluid">
      <ToastContainer />
      
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center my-4">
        <h4 className="mb-0">إضافة برومو كود</h4>
        <button
          className="btn btn-secondary d-flex align-items-center gap-2"
          onClick={() => navigate('/pharmacy-promo-codes')}
        >
          <FaTimes />
          إلغاء
        </button>
      </div>

      {/* Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card"
      >
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">
                  كود البرومو <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.code ? 'is-invalid' : ''}`}
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  placeholder="أدخل كود البرومو"
                />
                {errors.code && (
                  <div className="invalid-feedback">{errors.code}</div>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label">
                  نسبة الخصم (%) <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className={`form-control ${errors.discount_percent ? 'is-invalid' : ''}`}
                  name="discount_percent"
                  value={formData.discount_percent}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  min="1"
                  max="100"
                  step="1"
                  placeholder="أدخل نسبة الخصم (1-100)"
                />
                {errors.discount_percent && (
                  <div className="invalid-feedback">{errors.discount_percent}</div>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label">الحد الأدنى للطلب (جنيه)</label>
                <input
                  type="number"
                  className={`form-control ${errors.min_order ? 'is-invalid' : ''}`}
                  name="min_order"
                  value={formData.min_order}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  min="0"
                  step="0.01"
                  placeholder="أدخل الحد الأدنى للطلب"
                />
                {errors.min_order && (
                  <div className="invalid-feedback">{errors.min_order}</div>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label">الحد الأقصى للطلب (جنيه)</label>
                <input
                  type="number"
                  className={`form-control ${errors.max_order ? 'is-invalid' : ''}`}
                  name="max_order"
                  value={formData.max_order}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  min="0"
                  step="0.01"
                  placeholder="أدخل الحد الأقصى للطلب"
                />
                {errors.max_order && (
                  <div className="invalid-feedback">{errors.max_order}</div>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label">حد الاستخدام</label>
                <input
                  type="number"
                  className="form-control"
                  name="usage_limit"
                  value={formData.usage_limit}
                  onChange={handleInputChange}
                  min="1"
                  placeholder="أدخل حد الاستخدام (اتركه فارغ للاستخدام غير المحدود)"
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">
                  تاريخ البداية <span className="text-danger">*</span>
                </label>
                <input
                  type="datetime-local"
                  className={`form-control ${errors.starts_at ? 'is-invalid' : ''}`}
                  name="starts_at"
                  value={formData.starts_at}
                  onChange={handleInputChange}
                />
                {errors.starts_at && (
                  <div className="invalid-feedback">{errors.starts_at}</div>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label">
                  تاريخ الانتهاء <span className="text-danger">*</span>
                </label>
                <input
                  type="datetime-local"
                  className={`form-control ${errors.expires_at ? 'is-invalid' : ''}`}
                  name="expires_at"
                  value={formData.expires_at}
                  onChange={handleInputChange}
                />
                {errors.expires_at && (
                  <div className="invalid-feedback">{errors.expires_at}</div>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label">الحالة</label>
                <select
                  className="form-select"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value={1}>نشط</option>
                  <option value={0}>غير نشط</option>
                </select>
              </div>
            </div>

            <div className="row mt-4">
              <div className="col-12 d-flex gap-2 justify-content-center">
                <button
                  type="submit"
                  className="btn d-flex align-items-center gap-2" style={{ backgroundColor: 'rgb(24, 65, 148)', color: 'white' }}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">جاري الحفظ...</span>
                      </div>
                      جاري الحفظ...
                    </>
                  ) : (
                    <>
                      <FaSave />
                      حفظ
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary d-flex align-items-center gap-2"
                  onClick={() => navigate('/pharmacy-promo-codes')}
                >
                  <FaTimes />
                  إلغاء
                </button>
              </div>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default PharmacyAddPromoCode;
